// src/pages/Messages.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
  Spinner,
} from "react-bootstrap";

const LOCAL_STORAGE_KEY = "callbunker_contacts";

const normalizePhone = (phone) => {
  if (!phone) return "";
  return phone.replace(/[^\d+]/g, "").replace(/^00/, "+");
};

const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/[^\d]/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffSec = Math.floor((now - date) / 1000);

  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return date.toLocaleDateString();
};

function Messages({ user }) {
  const [conversations, setConversations] = useState([]);
  const [deviceContacts, setDeviceContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [initialMessage, setInitialMessage] = useState("");
  const [sendingNew, setSendingNew] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [threadToDelete, setThreadToDelete] = useState(null);
  const [deletingThread, setDeletingThread] = useState(false);

  const navigate = useNavigate();
  const pollTimerRef = useRef(null);

  // Load saved device contacts on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        setDeviceContacts(JSON.parse(saved));
      }
    } catch (e) {
      console.error("Failed to parse device contacts", e);
    }
  }, []);

  const fetchConversations = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/sms/conversations", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setConversations(res.data.conversations || []);
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations(true);

    // Poll every 5 seconds for new incoming texts
    pollTimerRef.current = setInterval(() => {
      fetchConversations(false);
    }, 5000);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // Helper to match a phone number against local device contacts or backend whitelist
  const resolveContact = (threadPhone, backendName) => {
    const cleanThread = normalizePhone(threadPhone).replace(/^\+1/, "").replace(/^\+/, "");

    // 1. Check local device contacts (match exact or suffix)
    if (deviceContacts.length > 0) {
      const match = deviceContacts.find((c) => {
        const cleanContact = normalizePhone(c.phone).replace(/^\+1/, "").replace(/^\+/, "");
        return (
          cleanContact &&
          cleanThread &&
          (cleanContact === cleanThread ||
            cleanThread.endsWith(cleanContact) ||
            cleanContact.endsWith(cleanThread))
        );
      });
      if (match && match.name && match.name !== "Unknown") {
        return { name: match.name, isVerified: true, hasContactName: true };
      }
    }

    // 2. Check backend name (ignore generic placeholders)
    if (
      backendName &&
      backendName !== "Verified Sender" &&
      backendName !== "Auto-added" &&
      backendName !== "Unknown"
    ) {
      return { name: backendName, isVerified: true, hasContactName: true };
    }

    // 3. Fallback to clean formatted phone number
    const isGenericVerified = backendName === "Verified Sender" || backendName === "Auto-added";
    return {
      name: formatPhoneNumber(threadPhone),
      isVerified: isGenericVerified,
      hasContactName: false,
    };
  };

  const handleConfirmDeleteConversation = async () => {
    if (!threadToDelete) return;
    setDeletingThread(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.delete(
        `/api/sms/conversations/${encodeURIComponent(threadToDelete.phoneNumber)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setConversations((prev) =>
          prev.filter(
            (c) =>
              c.conversationId !== threadToDelete.conversationId &&
              c.phoneNumber !== threadToDelete.phoneNumber
          )
        );
        setThreadToDelete(null);
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
      alert(err.response?.data?.message || "Failed to delete conversation. Please try again.");
    } finally {
      setDeletingThread(false);
    }
  };

  const handleStartConversation = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newRecipient.trim()) return;

    let cleanPhone = newRecipient.replace(/[^+0-9]/g, "");
    if (!cleanPhone.startsWith("+")) {
      cleanPhone = "+1" + cleanPhone;
    }

    if (initialMessage.trim()) {
      setSendingNew(true);
      setErrorMessage("");
      try {
        const token = localStorage.getItem("token");
        await axios.post(
          "/api/sms/send",
          { to: cleanPhone, body: initialMessage.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setShowNewModal(false);
        setNewRecipient("");
        setContactSearch("");
        setInitialMessage("");
        navigate(`/messages/${encodeURIComponent(cleanPhone)}`);
      } catch (err) {
        setErrorMessage(err.response?.data?.message || "Failed to send message");
      } finally {
        setSendingNew(false);
      }
    } else {
      setShowNewModal(false);
      setNewRecipient("");
      setContactSearch("");
      navigate(`/messages/${encodeURIComponent(cleanPhone)}`);
    }
  };

  // Filter conversations matching search
  const filteredConversations = conversations.filter((c) => {
    const term = searchTerm.toLowerCase();
    const resolved = resolveContact(c.phoneNumber, c.contactName);
    const nameMatch = resolved.name && resolved.name.toLowerCase().includes(term);
    const phoneMatch = c.phoneNumber && c.phoneNumber.includes(searchTerm);
    const msgMatch = c.lastMessage && c.lastMessage.toLowerCase().includes(term);
    return nameMatch || phoneMatch || msgMatch;
  });

  // Filter contacts inside the New Message modal
  const filteredModalContacts = deviceContacts.filter((c) => {
    if (!contactSearch.trim()) return true;
    const term = contactSearch.toLowerCase();
    const nameMatch = (c.name || "").toLowerCase().includes(term);
    const phoneMatch = (c.phone || "").includes(contactSearch);
    return nameMatch || phoneMatch;
  });

  const totalUnread = conversations.reduce((acc, c) => acc + (c.unreadCount || 0), 0);

  return (
    <Container className="mainWrapper px-3" style={{ paddingTop: "80px", paddingBottom: "90px" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h2 className="text-light fs-5 mb-0">
            <i className="fas fa-comment-dots me-2 text-success"></i>
            Messages
          </h2>
          {totalUnread > 0 && (
            <Badge bg="danger" pill>
              {totalUnread} new
            </Badge>
          )}
        </div>
        <Button
          variant="success"
          size="sm"
          className="d-flex align-items-center gap-2 px-3 py-2 fw-semibold shadow-sm"
          onClick={() => {
            setShowNewModal(true);
            setContactSearch("");
            setNewRecipient("");
            setInitialMessage("");
            setErrorMessage("");
          }}
        >
          <i className="fas fa-plus"></i>
          <span>New Message</span>
        </Button>
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text className="bg-dark border-secondary text-light">
            <i className="fas fa-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            className="bg-dark border-secondary text-light"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <Button
              variant="outline-secondary"
              onClick={() => setSearchTerm("")}
              className="text-light"
            >
              <i className="fas fa-times"></i>
            </Button>
          )}
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-success" role="status"></div>
        </div>
      ) : conversations.length === 0 ? (
        /* Empty state */
        <div className="text-center py-5 text-muted">
          <i className="fas fa-comments fa-3x text-muted mb-3"></i>
          <p className="mb-2">No messages yet.</p>
          <p className="small mb-3">
            Start a new text conversation or incoming texts will appear here.
          </p>
          <Button
            variant="success"
            size="sm"
            onClick={() => setShowNewModal(true)}
          >
            <i className="fas fa-plus me-1"></i>
            Start a Conversation
          </Button>
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className="text-center py-5 text-muted">
          No conversations matched "{searchTerm}"
        </div>
      ) : (
        /* Conversations List */
        <div className="d-flex flex-column gap-2">
          {filteredConversations.map((thread) => {
            const hasUnread = (thread.unreadCount || 0) > 0;
            const resolved = resolveContact(thread.phoneNumber, thread.contactName);
            const displayName = resolved.name;
            const initials = resolved.hasContactName
              ? resolved.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()
              : "#";

            return (
              <Card
                key={thread.conversationId}
                className={`border-0 shadow-sm text-light ${
                  hasUnread ? "bg-dark border-start border-4 border-success" : "bg-dark"
                }`}
                style={{
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
                onClick={() =>
                  navigate(`/messages/${encodeURIComponent(thread.phoneNumber)}`)
                }
              >
                <Card.Body className="p-3 d-flex align-items-center gap-3">
                  {/* Avatar */}
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                    style={{
                      width: "44px",
                      height: "44px",
                      backgroundColor: hasUnread ? "#198754" : "#495057",
                      fontSize: "0.95rem",
                    }}
                  >
                    {initials}
                  </div>

                  {/* Thread Info */}
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="d-flex justify-content-between align-items-baseline mb-1">
                      <div className="d-flex align-items-center gap-1 text-truncate">
                        <h6 className="mb-0 text-white text-truncate fw-semibold">
                          {displayName}
                        </h6>
                        {resolved.isVerified && (
                          <i
                            className="fas fa-check-circle text-success ms-1"
                            title="Verified Contact"
                            style={{ fontSize: "0.8rem" }}
                          ></i>
                        )}
                      </div>
                      <span
                        className="text-muted small ms-2 flex-shrink-0"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {formatTimeAgo(thread.lastMessageDate)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                      <p
                        className={`mb-0 small text-truncate ${
                          hasUnread ? "text-light fw-bold" : "text-muted"
                        }`}
                        style={{ maxWidth: "80%" }}
                      >
                        {thread.lastDirection === "outbound" && (
                          <span className="text-secondary me-1">You:</span>
                        )}
                        {thread.lastMessage}
                      </p>

                      {hasUnread && (
                        <Badge bg="success" pill style={{ fontSize: "0.7rem" }}>
                          {thread.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Actions: Delete Conversation */}
                  <div className="d-flex align-items-center flex-shrink-0">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-2 text-secondary"
                      style={{
                        borderRadius: "8px",
                        transition: "all 0.2s ease",
                        textDecoration: "none",
                        fontSize: "0.9rem",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#ef4444";
                        e.currentTarget.style.backgroundColor = "rgba(239, 68, 68, 0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#6c757d";
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                      title="Delete conversation"
                      onClick={(e) => {
                        e.stopPropagation();
                        setThreadToDelete({
                          ...thread,
                          displayName,
                        });
                      }}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal: New Message / Compose */}
      <Modal
        show={showNewModal}
        onHide={() => {
          setShowNewModal(false);
          setContactSearch("");
          setErrorMessage("");
        }}
        centered
        contentClassName="border-0 shadow-lg text-light"
      >
        <div style={{ backgroundColor: "#1e2433", borderRadius: "14px", overflow: "hidden" }}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{ borderBottom: "1px solid #334155", padding: "16px 20px" }}
          >
            <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
              <i className="fas fa-edit text-success"></i>
              <span>New Message</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3" style={{ color: "#f8fafc" }}>
            {errorMessage && (
              <div className="alert alert-danger py-2 small">{errorMessage}</div>
            )}

            {/* Recipient / Search Input */}
            <Form.Group className="mb-3">
              <Form.Label style={{ color: "#cbd5e1", fontSize: "0.85rem", fontWeight: "600", marginBottom: "6px" }}>
                To: Contact Name or Phone Number
              </Form.Label>
              <InputGroup>
                <InputGroup.Text style={{ backgroundColor: "#0f172a", borderColor: "#334155", color: "#94a3b8" }}>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  style={{
                    backgroundColor: "#0f172a",
                    borderColor: "#334155",
                    color: "#ffffff",
                    fontSize: "0.95rem",
                  }}
                  placeholder="Type phone number or search contact..."
                  value={newRecipient}
                  onChange={(e) => {
                    setNewRecipient(e.target.value);
                    setContactSearch(e.target.value);
                  }}
                  autoFocus
                />
              </InputGroup>
            </Form.Group>

            {/* Contact Suggestions List or Helpful Tip */}
            {filteredModalContacts.length > 0 ? (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#cbd5e1", fontSize: "0.82rem", fontWeight: "600" }}>
                    {contactSearch ? "Matching Contacts" : "Contacts"} ({filteredModalContacts.length})
                  </span>
                </div>
                <div
                  style={{
                    maxHeight: "260px",
                    overflowY: "auto",
                    borderRadius: "8px",
                    border: "1px solid #334155",
                    backgroundColor: "#0f172a",
                  }}
                  className="p-1"
                >
                  {filteredModalContacts.map((contact, idx) => (
                    <div
                      key={idx}
                      className="d-flex align-items-center justify-content-between p-2 rounded text-light"
                      style={{ cursor: "pointer", transition: "background 0.15s" }}
                      onClick={() => {
                        const clean = normalizePhone(contact.phone);
                        setShowNewModal(false);
                        setNewRecipient("");
                        setContactSearch("");
                        navigate(`/messages/${encodeURIComponent(clean)}`);
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1e293b")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <div className="d-flex align-items-center gap-2 overflow-hidden">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            backgroundColor: "#0d6efd",
                            fontSize: "0.85rem",
                          }}
                        >
                          {contact.name ? contact.name.slice(0, 2).toUpperCase() : "#"}
                        </div>
                        <div className="overflow-hidden">
                          <div className="text-white small fw-bold text-truncate">
                            {contact.name || "Unknown"}
                          </div>
                          <div style={{ color: "#94a3b8", fontSize: "0.78rem" }}>
                            {formatPhoneNumber(contact.phone)}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="success"
                        size="sm"
                        className="py-1 px-3 fw-semibold"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Text
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div
                className="p-2 px-3 rounded mb-3 text-light"
                style={{ backgroundColor: "#0f172a", border: "1px solid #334155", fontSize: "0.82rem" }}
              >
                <i className="fas fa-info-circle text-info me-2"></i>
                Enter any phone number with country code (e.g. <strong>+1508123456</strong>) and click <strong>Start Chat</strong>.
              </div>
            )}

            {/* Optional Opening Message Field */}
            <Form.Group className="mb-2">
              <Form.Label style={{ color: "#cbd5e1", fontSize: "0.85rem", fontWeight: "600", marginBottom: "6px" }}>
                Message (Optional)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                style={{
                  backgroundColor: "#0f172a",
                  borderColor: "#334155",
                  color: "#ffffff",
                  fontSize: "0.9rem",
                }}
                placeholder="Type an opening message..."
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid #334155", padding: "14px 20px" }}>
            <Button
              variant="outline-secondary"
              className="text-light px-3"
              size="sm"
              onClick={() => setShowNewModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              size="sm"
              className="px-4 fw-semibold shadow-sm"
              disabled={!newRecipient.trim() || sendingNew}
              onClick={handleStartConversation}
            >
              {sendingNew ? "Sending..." : "Start Chat"}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* Modal: Confirm Delete Conversation */}
      <Modal
        show={!!threadToDelete}
        onHide={() => !deletingThread && setThreadToDelete(null)}
        centered
        contentClassName="border-0 shadow-lg text-light"
      >
        <div style={{ backgroundColor: "#1e2433", borderRadius: "14px", overflow: "hidden" }}>
          <Modal.Header
            closeButton={!deletingThread}
            closeVariant="white"
            style={{ borderBottom: "1px solid #334155", padding: "16px 20px" }}
          >
            <Modal.Title className="fs-6 fw-bold text-white d-flex align-items-center gap-2">
              <i className="fas fa-trash-alt text-danger"></i>
              <span>Delete Conversation</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4" style={{ color: "#f8fafc" }}>
            <p className="mb-2">
              Are you sure you want to delete your conversation with{" "}
              <strong className="text-white">
                {threadToDelete?.displayName || threadToDelete?.phoneNumber}
              </strong>
              ?
            </p>
            <p className="text-secondary small mb-0">
              This will permanently delete all incoming and outgoing messages in this thread. This action cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid #334155", padding: "14px 20px" }}>
            <Button
              variant="outline-secondary"
              className="text-light px-3"
              size="sm"
              disabled={deletingThread}
              onClick={() => setThreadToDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="px-4 fw-semibold shadow-sm d-flex align-items-center gap-2"
              disabled={deletingThread}
              onClick={handleConfirmDeleteConversation}
            >
              {deletingThread ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-trash-alt"></i>
                  <span>Delete Conversation</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </Container>
  );
}

export default Messages;
